import invariant from 'invariant';

import config from 'Config';
import { SERVICE_WORKER_ENABLED, WEB_PUSH_SUPPORTED } from 'Lib/serviceWorkerHelper';

import notificationIcon from '../../../../public/static/android-chrome-192x192.png';
import notificationBadgeIcon from '../../../../public/static/notification-badge-icon.png';

export const NOTIFICATION_SUPPORTED = __CLIENT__ && 'Notification' in window;

export const PERMISSION_GRANTED = 'granted';
export const PERMISSION_DENIED = 'denied';

export const CHROME_PUSH_ENDPOINTS = [
  'https://android.googleapis.com/gcm/send/',
  'https://fcm.googleapis.com/fcm/send/',
];

export const getPermission = () => (NOTIFICATION_SUPPORTED ? Notification.permission : '');

export const canRequestPermission = () =>
  NOTIFICATION_SUPPORTED && getPermission() !== PERMISSION_DENIED;
export const permissionIsGranted = () => getPermission() === PERMISSION_GRANTED;
export const permissionIsDenied = () => getPermission() === PERMISSION_DENIED;

/**
 * Requests notification permission from the browser and returns a Promise
 *
 * Resolves with the permission status or rejects with an error if notifications are not supported
 * or permission is denied.
 */
export const requestPermission = (): Promise<string> => {
  if (!NOTIFICATION_SUPPORTED) {
    return Promise.reject(invariant(false, 'Notifications are not supported'));
  }
  return new Promise((resolve, reject) => {
    Notification.requestPermission((status: string) => {
      if (status === PERMISSION_DENIED) {
        reject(status);
      } else {
        resolve(status);
      }
    });
  });
};

/**
 * Gets the push notification subscription
 */
export const getPushSubscription = (): Promise<PushSubscription | undefined> => {
  if (!SERVICE_WORKER_ENABLED || !WEB_PUSH_SUPPORTED) {
    return Promise.resolve(undefined);
  }
  // A Promise that resolves to a PushSubscription object or undefined.
  return navigator.serviceWorker.ready
    .then((swReg) => swReg.pushManager.getSubscription())
    .then((subscription) => subscription || ({} as PushSubscription));
};

/**
 * Subscribes to push notifications
 */
export const subscribeToPush = () => {
  if (!SERVICE_WORKER_ENABLED || !WEB_PUSH_SUPPORTED) {
    return Promise.reject(invariant(false, 'Service Workers are not enabled'));
  }
  const publicKey = new Uint8Array(config.pushPublicKey);
  return navigator.serviceWorker.ready.then((swReg) =>
    swReg.pushManager.subscribe({
      applicationServerKey: publicKey,
      userVisibleOnly: true,
    })
  );
};

/**
 * Gets the push ID from the endpoint URL
 * Only Chrome is supported since we piggyback on the android push backend
 */
export const getPushIdFromEndpoint = (endpoint: string): string | undefined => {
  const isChromeEndpoint = CHROME_PUSH_ENDPOINTS.some((chromeEndpoint) =>
    endpoint.startsWith(chromeEndpoint)
  );

  if (!isChromeEndpoint) {
    return undefined;
  }
  return endpoint.split('/').pop();
};

// This is also used in public/handlePushNotifications.js
export const DEFAULT_NOTIFICATION_TITLE = 'Tinder';
export const notificationSubtypes = {
  ERROR: 'error',
  GENERIC: 'generic',
  NEW_MATCHES: 'match',
  NEW_MESSAGES: 'message',
  VERIFY_ERROR: 'verifyError',
  VERIFY_PENDING: 'verifyPending',
  VERIFY_SUCCESS: 'verifySuccess',
  VERIFY: 'verify',
  GOLD: 'gold',
  CONTACT_CARD_ERROR: 'contactCardError',
  LIKED_MESSAGE: 'messageLike',
};

export const commonOptions = {
  icon: notificationIcon,
  badge: notificationBadgeIcon,
  tag: 'default-tag',
};

export interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{
    outcome: 'accepted' | 'dismissed';
    platform: string;
  }>;
  prompt(): Promise<void>;
}

let beforeInstallPromptEvent: BeforeInstallPromptEvent | null;
export const getBeforeInstallPromptEvent = () => beforeInstallPromptEvent;
export const setBeforeInstallPromptEvent = (event: BeforeInstallPromptEvent) => {
  beforeInstallPromptEvent = event;
};
export const cleaBeforeInstallPromptEvent = () => {
  beforeInstallPromptEvent = null;
};
